import React, { useRef, useState, useLayoutEffect, useCallback } from 'react';

/* ProjectionChart — "stacked panels"
   Replaces the dual-axis Recharts chart. Two panels share one timeline and one
   crosshair: the WEALTH story (pot, top) and the INCOME story (£/yr, bottom).
   Both speak the same language — coloured lines over light shading, one hue per
   part. Life events are interactive lettered pins; hover one to read it.

   Pure SVG + React, no chart library. Consumes the existing `data` rows
   (totalNominal, totalReal, potEndNominal, potGrowth, potContribution,
   potDeductionLumpSum, potDeductionAnnuity, potDeductionWithdrawal,
   withdrawal, statePension, annuity, cashBuffer,
   taxPaid, netIncome, age, phase). */

// ── Colour system: one oklch ramp so income sources read as a family ─────────
const C = {
  ink: '#1b2330',
  muted: '#64748b',
  faint: '#94a3b8',
  grid: '#eceef2',
  potNominal: 'oklch(0.62 0.13 162)',   // emerald (brand)
  potReal: 'oklch(0.44 0.09 165)',      // deep emerald — "today's money"
  erosion: 'oklch(0.74 0.10 70 / 0.16)',// warm wash — value lost to inflation
  drawdown: 'oklch(0.62 0.13 162)',
  cashBuffer: 'oklch(0.64 0.12 215)',
  statePension: 'oklch(0.62 0.13 250)',
  dbPension: 'oklch(0.58 0.11 195)',
  annuity: 'oklch(0.60 0.13 290)',
};
const MARKER_COLOR = { milestone: C.ink, buffer: C.cashBuffer, income: C.statePension };

// ── Small helpers ────────────────────────────────────────────────────────────
function scale(d0, d1, r0, r1) {
  const m = (r1 - r0) / ((d1 - d0) || 1);
  return (v) => r0 + (v - d0) * m;
}
function niceMax(v) {
  if (v <= 0) return 1;
  const pow = Math.pow(10, Math.floor(Math.log10(v)));
  const n = v / pow;
  const step = n <= 1 ? 1 : n <= 2 ? 2 : n <= 2.5 ? 2.5 : n <= 3 ? 3 : n <= 4 ? 4 : n <= 5 ? 5 : 10;
  return step * pow;
}
const fmtK = (v) => {
  if (v === 0) return '£0';
  const n = Math.abs(v);
  if (n >= 1_000_000) {
    const m = n / 1_000_000;
    return `£${m % 1 === 0 ? Math.round(m) : m.toFixed(1)}m`;
  }
  if (n >= 1000) return `£${Math.round(n / 1000)}k`;
  return `£${Math.round(v)}`;
};
const fmtFull = (v) => '£' + Math.round(v || 0).toLocaleString();

// monotone cubic path (no overshoot)
function monotone(points) {
  const n = points.length;
  if (n === 0) return '';
  if (n === 1) return `M${points[0].x},${points[0].y}`;
  const xs = points.map((p) => p.x), ys = points.map((p) => p.y);
  const dx = [], dy = [], m = [];
  for (let i = 0; i < n - 1; i++) { dx[i] = xs[i + 1] - xs[i]; dy[i] = ys[i + 1] - ys[i]; m[i] = dy[i] / dx[i]; }
  const t = [m[0]];
  for (let i = 1; i < n - 1; i++) t[i] = m[i - 1] * m[i] <= 0 ? 0 : (m[i - 1] + m[i]) / 2;
  t[n - 1] = m[n - 2];
  for (let i = 0; i < n - 1; i++) {
    if (m[i] === 0) { t[i] = 0; t[i + 1] = 0; }
    else {
      const a = t[i] / m[i], b = t[i + 1] / m[i], h = Math.hypot(a, b);
      if (h > 3) { const s = 3 / h; t[i] = s * a * m[i]; t[i + 1] = s * b * m[i]; }
    }
  }
  let d = `M${xs[0]},${ys[0]}`;
  for (let i = 0; i < n - 1; i++) {
    const h = dx[i];
    d += `C${xs[i] + h / 3},${ys[i] + t[i] * h / 3} ${xs[i + 1] - h / 3},${ys[i + 1] - t[i + 1] * h / 3} ${xs[i + 1]},${ys[i + 1]}`;
  }
  return d;
}
function areaPath(points, baseY) {
  if (!points.length) return '';
  const last = points[points.length - 1], first = points[0];
  return `${monotone(points)}L${last.x},${baseY}L${first.x},${baseY}Z`;
}
function bandPath(upper, lower) {
  if (!upper.length) return '';
  const down = monotone(lower.slice().reverse());
  return `${monotone(upper)}L${down.slice(1)}Z`;
}

// ── Measure host element (width + height) ────────────────────────────────────
function useMeasure() {
  const ref = useRef(null);
  const [box, setBox] = useState({ w: 0, h: 0 });
  useLayoutEffect(() => {
    if (!ref.current) return;
    const ro = new ResizeObserver((e) => setBox({ w: e[0].contentRect.width, h: e[0].contentRect.height }));
    ro.observe(ref.current);
    const r = ref.current.getBoundingClientRect();
    setBox({ w: r.width, h: r.height });
    return () => ro.disconnect();
  }, []);
  return [ref, box];
}

// ── Interactive marker pins ───────────────────────────────────────────────────
function MarkerPins({ events, xScale, cy }) {
  const [hot, setHot] = useState(null);
  // nudge exact/near collisions apart horizontally
  const placed = events.slice().sort((a, b) => a.age - b.age).reduce((acc, ev) => {
    let x = xScale(ev.age);
    const prev = acc[acc.length - 1];
    if (prev && x - prev.x < 21) x = prev.x + 21;
    acc.push({ ev, x });
    return acc;
  }, []);
  return placed.map(({ ev, x }) => {
    const color = MARKER_COLOR[ev.kind] || C.muted;
    const isHot = hot === ev.id;
    const flip = x > xScale.right - 90;
    return (
      <div
        key={ev.id}
        onMouseEnter={() => setHot(ev.id)}
        onMouseLeave={() => setHot((h) => (h === ev.id ? null : h))}
        style={{ position: 'absolute', left: x, top: cy, transform: 'translate(-50%,-50%)', pointerEvents: 'auto', zIndex: isHot ? 6 : 3 }}
      >
        <div style={{
          width: 19, height: 19, borderRadius: 999, background: isHot ? color : '#fff',
          border: `1.5px solid ${color}`, color: isHot ? '#fff' : color,
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          fontSize: 11, fontWeight: 700, lineHeight: 1, boxShadow: '0 1px 2px rgba(16,24,40,0.10)',
          transition: 'background .12s, color .12s',
        }}>{ev.letter}</div>
        {isHot && (
          <div style={{
            position: 'absolute', top: '50%', [flip ? 'right' : 'left']: 25, transform: 'translateY(-50%)',
            whiteSpace: 'nowrap', background: C.ink, color: '#fff', fontSize: 11, fontWeight: 500,
            padding: '3px 8px', borderRadius: 6, boxShadow: '0 4px 14px rgba(16,24,40,0.18)',
          }}>{ev.label}</div>
        )}
      </div>
    );
  });
}

export default function ProjectionChart({
  data,
  currentAge,
  retirementAge,
  statePensionAge,
  partnerStatePensionAge = statePensionAge,
  includePartnerStatePension = false,
  lifeExpectancy,
  cashBufferEnabled = false,
  cashBufferSpreadStartEffective = null,
  cashBufferMarkerAge = null,
  annuityEnabled = false,
  dbPensionEnabled = false,
  dbPensionAge = 65,
  includeStatePension = true,
  showIncome = true,
  incomeOverrides = [],
}) {
  const [ref, { w, h }] = useMeasure();
  const [idx, setIdx] = useState(null);
  const [dragging, setDragging] = useState(false);

  const rows = (data || []).filter((d) => d.age <= lifeExpectancy);
  const ages = rows.map((d) => d.age);

  const sources = [
    { key: 'withdrawal', label: 'Drawdown', color: C.drawdown },
    { key: 'taxFreeLumpSum', label: 'Tax-free lump sum', color: 'oklch(0.64 0.12 215)' },
    { key: 'cashBuffer', label: 'Cash buffer', color: C.cashBuffer },
    { key: 'statePension', label: 'State pension', color: C.statePension },
    { key: 'dbPension', label: 'DB pension', color: C.dbPension },
    { key: 'annuity', label: 'Annuity', color: C.annuity },
  ];
  const grossOf = (d) => sources.reduce((s, x) => s + (d[x.key] || 0), 0);
  const incomeRows = rows.filter((d) => grossOf(d) > 0);
  const hasIncome = showIncome && incomeRows.length > 1;

  // ── geometry ──
  const left = 56, right = Math.max(w - 16, 80), top = 30, bottom = 26;
  const innerTop = top, innerBottom = Math.max(h - bottom, top + 120);
  let potTop = innerTop, potBottom = innerBottom, incTop = 0, incBottom = 0;
  if (hasIncome) {
    const avail = innerBottom - innerTop;
    const incH = Math.min(150, Math.max(94, avail * 0.36));
    const gap = 30;
    potBottom = innerBottom - incH - gap;
    incTop = potBottom + gap;
    incBottom = innerBottom;
  }

  const xS = scale(ages[0], ages[ages.length - 1], left, right);
  xS.right = right;

  const potMax = niceMax(Math.max(1, ...rows.map((d) => d.totalNominal || 0)));
  const potY = scale(0, potMax, potBottom, potTop);
  const potTicks = [0, 0.5, 1].map((f) => f * potMax);

  const incMax = hasIncome ? niceMax(Math.max(1, ...incomeRows.map(grossOf))) : 1;
  const incY = scale(0, incMax, incBottom, incTop);
  const incTicks = [0, 0.5, 1].map((f) => f * incMax);

  // ── life-event markers (consistent system) ──
  const events = [];
  if (
    cashBufferEnabled
    && cashBufferSpreadStartEffective != null
    && cashBufferSpreadStartEffective >= currentAge
    && cashBufferSpreadStartEffective < retirementAge
  ) {
    events.push({
      id: 'early',
      age: cashBufferSpreadStartEffective,
      label: `Early retire ${cashBufferSpreadStartEffective}`,
      letter: 'E',
      kind: 'milestone',
    });
  }
  events.push({ id: 'retire', age: retirementAge, label: `Retire ${retirementAge}`, letter: 'R', kind: 'milestone' });
  if (
    cashBufferEnabled
    && cashBufferMarkerAge != null
    && cashBufferMarkerAge > retirementAge
    && cashBufferMarkerAge <= lifeExpectancy
  ) {
    events.push({
      id: 'buffer',
      age: cashBufferMarkerAge,
      label: `Cash buffer ends ${cashBufferMarkerAge}`,
      letter: 'C',
      kind: 'buffer',
    });
  }
  if (includeStatePension && statePensionAge >= currentAge && statePensionAge <= lifeExpectancy) {
    events.push({
      id: 'state',
      age: statePensionAge,
      label: includePartnerStatePension && partnerStatePensionAge !== statePensionAge
        ? `Your state pension ${statePensionAge}`
        : `State pension ${statePensionAge}`,
      letter: 'S',
      kind: 'income',
    });
  }
  if (
    includeStatePension
    && includePartnerStatePension
    && partnerStatePensionAge >= currentAge
    && partnerStatePensionAge <= lifeExpectancy
    && partnerStatePensionAge !== statePensionAge
  ) {
    events.push({
      id: 'state-partner',
      age: partnerStatePensionAge,
      label: `Partner state pension ${partnerStatePensionAge}`,
      letter: 'P',
      kind: 'income',
    });
  }
  if (dbPensionEnabled && dbPensionAge >= currentAge && dbPensionAge <= lifeExpectancy) {
    events.push({
      id: 'db',
      age: dbPensionAge,
      label: `DB pension ${dbPensionAge}`,
      letter: 'D',
      kind: 'income',
    });
  }
  if (annuityEnabled) {
    const a = incomeRows.find((d) => (d.annuity || 0) > 0);
    if (a) events.push({ id: 'annuity', age: a.age, label: `Annuity ${a.age}`, letter: 'A', kind: 'income' });
  }
  incomeOverrides.forEach((period, index) => {
    if (period.fromAge < ages[0] || period.fromAge > ages[ages.length - 1]) return;
    const netLabel = `£${Math.round(period.net / 1000)}k target`;
    events.push({
      id: `custom-target-${index}`,
      age: period.fromAge,
      label: period.fromAge === period.toAge
        ? `${netLabel} at ${period.fromAge}`
        : `${netLabel} ${period.fromAge}–${period.toAge}`,
      letter: 'T',
      kind: 'milestone',
    });
  });
  events.push({ id: 'plan', age: lifeExpectancy, label: `Plan to ${lifeExpectancy}`, letter: 'P', kind: 'milestone' });
  const markers = events.filter((e) => e.age >= ages[0] && e.age <= ages[ages.length - 1]);

  // ── line geometry ──
  const ready = w > 0 && ages.length > 1;
  const nom = rows.map((d) => ({ x: xS(d.age), y: potY(d.totalNominal || 0) }));
  const real = rows.map((d) => ({ x: xS(d.age), y: potY(d.totalReal || 0) }));

  const bands = [];
  if (hasIncome) {
    let acc = incomeRows.map(() => 0);
    sources.forEach((s) => {
      const lower = acc.map((v, i) => ({ x: xS(incomeRows[i].age), y: incY(v) }));
      acc = acc.map((v, i) => v + (incomeRows[i][s.key] || 0));
      const upper = acc.map((v, i) => ({ x: xS(incomeRows[i].age), y: incY(v) }));
      bands.push({ s, upper, lower });
    });
  }

  const targetRows = rows.filter((d) => (d.targetNet || 0) > 0);
  const showTargetLine = hasIncome && incomeOverrides.length > 0 && targetRows.length > 1;
  const targetLine = showTargetLine
    ? targetRows.map((d) => ({ x: xS(d.age), y: incY(d.targetNet) }))
    : [];
  const customTargetBands = hasIncome
    ? incomeOverrides.map((period) => {
      const start = Math.max(ages[0], period.fromAge);
      const end = Math.min(ages[ages.length - 1], period.toAge);
      if (end < start) return null;
      return { start, end };
    }).filter(Boolean)
    : [];

  // ── hover ──
  const setNearestIdxFromClientX = useCallback((clientX, currentTarget) => {
    if (!ages.length) return;
    const r = currentTarget.getBoundingClientRect();
    const mx = clientX - r.left;
    let best = 0, bd = Infinity;
    for (let i = 0; i < ages.length; i++) {
      const d = Math.abs(xS(ages[i]) - mx);
      if (d < bd) { bd = d; best = i; }
    }
    setIdx(best);
  }, [ages, xS]);
  const onMove = useCallback((e) => {
    setNearestIdxFromClientX(e.clientX, e.currentTarget);
  }, [setNearestIdxFromClientX]);
  const onLeave = useCallback(() => setIdx(null), []);

  const onPointerDown = useCallback((e) => {
    // Enable touch drag scrubbing on mobile; prevent text selection/scroll capture.
    if (!e.currentTarget) return;
    try { e.currentTarget.setPointerCapture(e.pointerId); } catch (_) { /* ignore */ }
    setDragging(true);
    setNearestIdxFromClientX(e.clientX, e.currentTarget);
  }, [setNearestIdxFromClientX]);
  const onPointerMove = useCallback((e) => {
    if (!dragging) return;
    setNearestIdxFromClientX(e.clientX, e.currentTarget);
  }, [dragging, setNearestIdxFromClientX]);
  const onPointerUp = useCallback((e) => {
    if (!e.currentTarget) return;
    try { e.currentTarget.releasePointerCapture(e.pointerId); } catch (_) { /* ignore */ }
    setDragging(false);
    // keep last selected year on touch
  }, []);
  const onPointerCancel = useCallback(() => setDragging(false), []);

  const d = idx != null ? rows[idx] : null;
  const dHasIncome = d && grossOf(d) > 0;
  const potDeductions = d ? [
    { key: 'potDeductionLumpSum', label: 'Tax-free lump sum' },
    { key: 'potDeductionAnnuity', label: 'Annuity purchase' },
  ].filter((s) => (d[s.key] || 0) > 0) : [];
  const potHasGrowth = d && (d.potGrowth || 0) > 0;
  const potHasContribution = d && (d.potContribution || 0) > 0;
  const potHasMovement = potDeductions.length > 0 || potHasGrowth || potHasContribution;
  const tipFlip = d ? xS(d.age) > right - 180 : false;

  // x labels
  const ageSpan = ages.length ? ages[ages.length - 1] - ages[0] : 1;
  const rawStep = ageSpan / 12;
  const labelStep = rawStep <= 1 ? 1 : rawStep <= 2 ? 2 : rawStep <= 5 ? 5 : 10;

  return (
    <div ref={ref} style={{ position: 'relative', width: '100%', height: '100%', minHeight: 320 }}>
      {ready && (
        <svg
          width={w}
          height={h}
          style={{ display: 'block', touchAction: 'none', userSelect: 'none' }}
          onMouseMove={onMove}
          onMouseLeave={onLeave}
          onPointerDown={onPointerDown}
          onPointerMove={onPointerMove}
          onPointerUp={onPointerUp}
          onPointerCancel={onPointerCancel}
        >
          <defs>
            <linearGradient id="pc-pot" x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor={C.potNominal} stopOpacity={0.16} />
              <stop offset="100%" stopColor={C.potNominal} stopOpacity={0.01} />
            </linearGradient>
          </defs>

          {/* shared guide lines through both panels */}
          {markers.map((ev) => (
            <line key={ev.id} x1={xS(ev.age)} x2={xS(ev.age)} y1={potTop} y2={hasIncome ? incBottom : potBottom}
              stroke={MARKER_COLOR[ev.kind] || C.muted} strokeOpacity={0.18} strokeWidth={1} strokeDasharray="3 4" />
          ))}

          {/* panel labels */}
          <text x={left} y={potTop + 12} fontSize={11} fontWeight={600} fill={C.muted} letterSpacing="0.06em">POT VALUE (START OF YEAR)</text>
          {hasIncome && <text x={left} y={incTop - 9} fontSize={11} fontWeight={600} fill={C.muted} letterSpacing="0.06em">INCOME EACH YEAR</text>}

          {/* ── pot panel ── */}
          {potTicks.map((t, i) => (
            <g key={'pt' + i}>
              <line x1={left} x2={right} y1={potY(t)} y2={potY(t)} stroke={C.grid} strokeWidth={1} />
              <text x={left - 10} y={potY(t) + 3.5} textAnchor="end" fontSize={11} fill={C.faint}
                fontFamily="ui-monospace, SFMono-Regular, Menlo, monospace">{fmtK(t)}</text>
            </g>
          ))}
          <path d={bandPath(nom, real)} fill={C.erosion} />
          <path d={areaPath(nom, potBottom)} fill="url(#pc-pot)" />
          <path d={monotone(nom)} fill="none" stroke={C.potNominal} strokeWidth={2.25} />
          <path d={monotone(real)} fill="none" stroke={C.potReal} strokeWidth={1.6} strokeOpacity={0.9} />

          {/* ── income panel ── */}
          {hasIncome && incTicks.map((t, i) => (
            <g key={'it' + i}>
              <line x1={left} x2={right} y1={incY(t)} y2={incY(t)} stroke={C.grid} strokeWidth={1} />
              <text x={left - 10} y={incY(t) + 3.5} textAnchor="end" fontSize={11} fill={C.faint}
                fontFamily="ui-monospace, SFMono-Regular, Menlo, monospace">{fmtK(t)}</text>
            </g>
          ))}
          {hasIncome && customTargetBands.map((band, i) => (
            <rect
              key={`custom-target-band-${i}`}
              x={xS(band.start)}
              y={incTop}
              width={Math.max(0, xS(band.end) - xS(band.start))}
              height={incBottom - incTop}
              fill="oklch(0.62 0.13 162 / 0.08)"
            />
          ))}
          {hasIncome && bands.map((b, i) => <path key={'bf' + i} d={bandPath(b.upper, b.lower)} fill={b.s.color} fillOpacity={0.26} />)}
          {hasIncome && bands.map((b, i) => <path key={'bl' + i} d={monotone(b.upper)} fill="none" stroke={b.s.color} strokeWidth={1.85} />)}
          {showTargetLine && (
            <path
              d={monotone(targetLine)}
              fill="none"
              stroke="oklch(0.55 0.10 162)"
              strokeWidth={1.6}
              strokeDasharray="5 4"
              strokeOpacity={0.9}
            />
          )}

          {/* crosshair + focus dots */}
          {d && <line x1={xS(d.age)} x2={xS(d.age)} y1={potTop} y2={hasIncome ? incBottom : potBottom} stroke={C.ink} strokeOpacity={0.16} strokeWidth={1} />}
          {d && <circle cx={xS(d.age)} cy={potY(d.totalNominal || 0)} r={4} fill={C.potNominal} />}
          {d && <circle cx={xS(d.age)} cy={potY(d.totalReal || 0)} r={3} fill={C.potReal} fillOpacity={0.75} />}
          {d && hasIncome && dHasIncome && <circle cx={xS(d.age)} cy={incY(grossOf(d))} r={3.5} fill={C.drawdown} />}

          {/* x axis */}
          {ages.filter((a) => a % labelStep === 0).map((a) => (
            <text key={a} x={xS(a)} y={(hasIncome ? incBottom : potBottom) + 18} textAnchor="middle" fontSize={11}
              fill={C.faint} fontFamily="ui-monospace, monospace">{a}</text>
          ))}
        </svg>
      )}

      {/* marker pins */}
      {ready && (
        <div style={{ position: 'absolute', inset: 0, pointerEvents: 'none' }}>
          <MarkerPins events={markers} xScale={xS} cy={potTop - 14} />
        </div>
      )}

      {/* tooltip */}
      {ready && d && (
        <div style={{
          position: 'absolute', left: xS(d.age), top: potTop + 2, zIndex: 20, pointerEvents: 'none',
          transform: tipFlip ? 'translateX(calc(-100% - 14px))' : 'translateX(14px)',
          minWidth: 196, background: '#fff', border: '1px solid #e7e9ee', borderRadius: 11,
          padding: '11px 13px', boxShadow: '0 8px 28px rgba(16,24,40,0.12)', fontSize: 12.5,
          fontFamily: 'ui-sans-serif, system-ui, sans-serif',
        }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', gap: 14, whiteSpace: 'nowrap', fontWeight: 600, color: C.ink, paddingBottom: 8, marginBottom: 8, borderBottom: '1px solid #f0f1f4' }}>
            <span>Age {d.age}</span>
            <span style={{ fontSize: 11, fontWeight: 500, color: C.faint, fontFamily: 'ui-monospace, monospace' }}>
              {new Date().getFullYear() + (d.age - currentAge)}
            </span>
          </div>
          <Row label="Pot value" labelColor={C.potNominal} value={fmtFull(d.totalNominal)} bold />
          <Row label="Today's money" sub value={fmtFull(d.totalReal)} />
          {potHasMovement && (
            <div style={{ marginTop: 8, paddingTop: 8, borderTop: '1px solid #f0f1f4' }}>
              {potHasGrowth && <Row label="+ Growth" value={fmtFull(d.potGrowth)} />}
              {potHasContribution && <Row label="+ Contribution" value={fmtFull(d.potContribution)} />}
              {potDeductions.map((s) => (
                <Row key={s.key} label={`− ${s.label}`} value={fmtFull(d[s.key])} />
              ))}
            </div>
          )}
          {dHasIncome && (
            <div style={{ marginTop: 8, paddingTop: 8, borderTop: '1px solid #f0f1f4' }}>
              <div style={{ fontSize: 11, fontWeight: 600, color: C.muted, letterSpacing: '0.04em', marginBottom: 6 }}>INCOME</div>
              {sources.filter((s) => (d[s.key] || 0) > 0).map((s) => (
                <Row key={s.key} dot={s.color} label={s.label} labelColor={s.color} value={fmtFull(d[s.key])} bold />
              ))}
              <Row label="− Tax" sub value={fmtFull(d.taxPaid || 0)} />
              <div style={{ display: 'flex', justifyContent: 'space-between', gap: 18, marginTop: 6, paddingTop: 7, borderTop: '1px solid #f0f1f4', color: '#059669', fontWeight: 600 }}>
                <span>Net to you</span>
                <span style={{ fontFamily: 'ui-monospace, monospace' }}>{fmtFull(d.netIncome)}</span>
              </div>
              {(d.targetNet || 0) > 0 && d.customTarget && (
                <Row label="Target net" sub value={fmtFull(d.targetNet)} />
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function Row({ label, value, dot, labelColor, bold, sub }) {
  return (
    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 18, padding: '2px 0',
      color: sub ? '#94a3b8' : '#64748b', fontSize: sub ? 11 : 12.5 }}>
      <span style={{ display: 'inline-flex', alignItems: 'center', color: labelColor || undefined }}>
        {dot && <i style={{ width: 7, height: 7, borderRadius: 999, background: dot, marginRight: 7, display: 'inline-block' }} />}
        {label}
      </span>
      <span style={{ color: bold ? C.ink : undefined, fontWeight: bold ? 600 : 400, fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace', fontSize: 12 }}>{value}</span>
    </div>
  );
}
